import axios from 'axios';

// Create an axios instance for the tours API
const toursApi = axios.create({
  baseURL: process.env.REACT_APP_TOURS_API_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
toursApi.interceptors.request.use(
  (config) => {
    const token = process.env.REACT_APP_TOURS_API_TOKEN;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Mock mode disabled
const MOCK_MODE = false;

/**
 * Fetch tours with optional filtering
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Number of items per page
 * @param {string} params.source - Source of tours ('all', 'local', 'viator')
 * @param {string} params.search - Search term
 * @param {number} params.minPrice - Minimum price
 * @param {number} params.maxPrice - Maximum price
 * @returns {Promise<Object>} - Tours data
 */
export const fetchTours = async (params = {}) => {
  try {
    const response = await toursApi.get('/services', {
      params: {
        page: params.page || 1,
        limit: params.limit || 10,
        source: params.source || 'all',
        ...(params.search && { search: params.search }),
        ...(params.minPrice && { minPrice: params.minPrice }),
        ...(params.maxPrice && { maxPrice: params.maxPrice }),
      },
    });

    // Process tours to add missing IDs and clean up data
    const rawTours = response.data.data || response.data.services || [];
    const tours = processTours(rawTours);

    return {
      tours,
      pagination: {
        total: response.data.total || response.data.pagination?.total || 0,
        page: response.data.page || response.data.pagination?.currentPage || 1,
        limit: response.data.limit || response.data.pagination?.perPage || 10,
        pages: response.data.pages || response.data.pagination?.pages || 1,
      },
    };
  } catch (error) {
    console.error('Error fetching tours:', error);
    throw error;
  }
};

/**
 * Process tours to ensure they have required fields
 * @param {Array} tours - Array of tour objects
 * @returns {Array} - Processed tours with required fields
 */
const processTours = (tours) => {
  return tours.map((tour, index) => {
    // Add _id if missing
    if (!tour._id) {
      if (tour.viatorData?.productCode) {
        tour._id = tour.viatorData.productCode;
      } else {
        tour._id = `tour-${Date.now()}-${index}`;
      }
    }

    // Ensure tour has a name - use productCode or generate one if missing
    if (!tour.name && tour.viatorData?.productCode) {
      tour.name = `Tour ${tour.viatorData.productCode}`;
    } else if (!tour.name) {
      tour.name = `Tour ${tour._id}`;
    }

    return tour;
  });
};

/**
 * Fetch a specific tour by ID
 * @param {string} tourId - Tour ID
 * @param {string} source - Source of the tour ('local', 'viator')
 * @returns {Promise<Object>} - Tour data
 */
export const fetchTourById = async (tourId, source = 'local') => {
  try {
    let endpoint = '';
    
    // Construct the appropriate endpoint based on the source
    if (source === 'viator') {
      endpoint = `/viator/products/${tourId}`;
      console.log(`Fetching Viator tour with ID: ${tourId}`);
    } else {
      endpoint = `/services/${tourId}`;
      console.log(`Fetching local tour with ID: ${tourId}`);
    }
    
    console.log(`Making API request to: ${endpoint}`);
    
    // Add a timeout to the request to prevent hanging
    const response = await toursApi.get(endpoint, { 
      timeout: 10000,
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    console.log('API response status:', response.status);
    
    // Check if we have data
    if (!response.data) {
      console.warn('No data in response:', response);
      throw new Error('No data received from API');
    }
    
    // Handle different response formats
    let tourData;
    if (source === 'viator') {
      // Viator API might have a different response structure
      tourData = response.data;
      console.log('Viator tour data received');
      
      // Ensure Viator tours have an _id
      if (!tourData._id && tourData.viatorData?.productCode) {
        tourData._id = tourData.viatorData.productCode;
      } else if (!tourData._id) {
        tourData._id = `viator-${tourId}`;
      }
    } else {
      // Local API response structure
      tourData = response.data.data || response.data;
      console.log('Local tour data received');
    }
    
    // Validate the tour data
    if (!tourData || (typeof tourData === 'object' && Object.keys(tourData).length === 0)) {
      console.warn('Empty tour data received:', tourData);
      throw new Error('Empty tour data received');
    }
    
    // Add source information if not present
    if (!tourData.source) {
      tourData.source = source;
    }
    
    console.log('Tour data processed successfully');
    return tourData;
  } catch (error) {
    console.error(`Error fetching tour ${tourId} from ${source}:`, error);
    
    // Add more detailed error logging
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response status:', error.response.status);
      console.error('Error response data:', error.response.data);
      throw new Error(`API error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received, request details:', error.request);
      throw new Error('No response received from server. Please check your connection and try again.');
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error message:', error.message);
      throw error;
    }
  }
};

/**
 * Fetch tours by category
 * @param {string} category - Category name
 * @param {Object} params - Additional query parameters
 * @returns {Promise<Object>} - Tours data
 */
export const fetchToursByCategory = async (category, params = {}) => {
  try {
    const response = await toursApi.get('/services', {
      params: {
        category,
        page: params.page || 1,
        limit: params.limit || 10,
        source: params.source || 'all',
      },
    });

    // Process tours to add missing IDs and clean up data
    const rawTours = response.data.data || response.data.services || [];
    const tours = processTours(rawTours);

    return {
      tours,
      pagination: {
        total: response.data.total || response.data.pagination?.total || 0,
        page: response.data.page || response.data.pagination?.currentPage || 1,
        limit: response.data.limit || response.data.pagination?.perPage || 10,
        pages: response.data.pages || response.data.pagination?.pages || 1,
      },
    };
  } catch (error) {
    console.error(`Error fetching tours for category ${category}:`, error);
    throw error;
  }
};

export default {
  fetchTours,
  fetchTourById,
  fetchToursByCategory,
}; 